import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  CircularProgress,
  Chip,
  useTheme,
} from '@mui/material';
import { FileDownload, People } from '@mui/icons-material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from 'material-react-table';
import { EnrolledUser } from '@/constants/types';
import { eventService } from '@/services/events/eventService';

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

export const EnrolledUsersPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const theme = useTheme();

  const [eventName, setEventName] = useState('');
  const [users, setUsers] = useState<EnrolledUser[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const loadUsers = useCallback(async () => {
    if (!eventId) return;
    setIsLoading(true);
    try {
      const res = await eventService.getEnrolledUsers(eventId, paginationState.pageIndex + 1, paginationState.pageSize);
      setUsers(res.data.users);
      setTotal(res.data.pagination.total);
    } catch {
      // handled by interceptor
    } finally {
      setIsLoading(false);
    }
  }, [eventId, paginationState]);

  // Load event name for the header and CSV filename
  useEffect(() => {
    if (!eventId) return;
    eventService.getEvent(eventId)
      .then((res) => setEventName(res.data.eventName))
      .catch(() => {});
  }, [eventId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleExport = async () => {
    if (!eventId) return;
    setIsExporting(true);
    try {
      const blob = await eventService.exportEnrolledUsers(eventId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${eventName || eventId}_enrollments.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // handled by interceptor
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo<MRT_ColumnDef<EnrolledUser>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 180,
        Cell: ({ cell }) => cell.getValue<string>() || '—',
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 220,
        Cell: ({ cell }) => cell.getValue<string>() || '—',
      },
      {
        accessorKey: 'designation',
        header: 'Designation',
        size: 150,
        Cell: ({ cell }) => cell.getValue<string>() || '—',
      },
      {
        accessorKey: 'city',
        header: 'City',
        size: 120,
        Cell: ({ cell }) => cell.getValue<string>() || '—',
      },
      {
        accessorKey: 'state',
        header: 'State',
        size: 130,
        Cell: ({ cell }) => cell.getValue<string>() || '—',
      },
      {
        accessorKey: 'whatsappNumber',
        header: 'WhatsApp',
        size: 130,
        Cell: ({ cell }) => cell.getValue<string>() || '—',
      },
      {
        accessorKey: 'enrolledAt',
        header: 'Enrolled At',
        size: 180,
        Cell: ({ cell }) => formatDate(cell.getValue<string>()),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: users,
    enableRowSelection: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: true,
    enableSorting: true,
    enableBottomToolbar: true,
    enableTopToolbar: false,
    manualPagination: true,
    rowCount: total,
    state: {
      pagination: paginationState,
      isLoading,
    },
    onPaginationChange: setPaginationState,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => window.open(`/profile/${row.original._id}`, '_blank'),
      sx: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 600,
        backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 25, 50],
      shape: 'rounded',
      variant: 'outlined',
    },
    renderEmptyRowsFallback: () => (
      <Box
        sx={{
          textAlign: 'center',
          py: 8,
        }}
      >
        <People sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">No enrollments yet</Typography>
      </Box>
    ),
  });

  if (isLoading && users.length === 0) {
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Skeleton width={200} height={32} />
          <Skeleton width={120} height={40} />
        </Box>
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} height={52} sx={{ mb: 0.5 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Enrolled Users
          </Typography>
          {eventName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {eventName}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {total > 0 && (
            <Chip
              icon={<People sx={{ fontSize: 16 }} />}
              label={`${total} enrolled`}
              color="primary"
              variant="outlined"
            />
          )}
          <Button
            variant="contained"
            startIcon={isExporting ? <CircularProgress size={14} color="inherit" /> : <FileDownload />}
            onClick={handleExport}
            disabled={isExporting || total === 0}
            sx={{ textTransform: 'none' }}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <MaterialReactTable table={table} />
    </Box>
  );
};
